// src/components/shared/ProfileCard.js - VERSIÓN CORREGIDA SIN CÓDIGO DE POSTSss
import React, { memo, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FiMapPin, 
  FiMessageSquare, 
  FiStar, 
  FiUser, 
  FiBriefcase, 
  FiExternalLink,
  FiGlobe,
  FiUsers,
  FiAward,
  FiTrendingUp
} from 'react-icons/fi';
import FollowButton from './FollowButton';
import FlagBadge from '../common/FlagBadge';

// ✅ Mapa de traducción de servicios de inglés a español
const SERVICE_TRANSLATIONS = {
  // Fabricantes
  'manufacturer_audio': 'Fabricante de Audio',
  'manufacturer_lighting': 'Fabricante de Iluminación', 
  'manufacturer_video': 'Fabricante de Video',
  'manufacturer_structures': 'Fabricante de Estructuras',
  'manufacturer_cabling': 'Fabricante de Cableado',
  'manufacturer_control': 'Fabricante de Control',
  
  // Distribuidores
  'distributor_official_rep': 'Representante Oficial',
  'distributor_wholesaler': 'Mayorista',
  'distributor_regional': 'Distribuidor Regional',
  
  // Integradores
  'integrator_corporate': 'Integrador Corporativo',
  'integrator_fixed': 'Instalador de Sistemas',
  'integrator_specialized': 'Integrador Especializado',
  'integrator_residential': 'Instalación Residencial',
  
  // Alquiler y Producción
  'rental_live_sound': 'Sonido en Vivo',
  'rental_live_video': 'Video en Vivo',
  'rental_lighting': 'Iluminación para Eventos',
  'rental_full_production': 'Producción Integral',
  'rental_backline': 'Backline',
  
  // Servicios Especializados
  'services_acoustic_design': 'Diseño Acústico',
  'services_system_design': 'Diseño de Sistemas AV',
  'services_consulting': 'Consultoría Técnica',
  'services_post_production': 'Postproducción',
  'services_maintenance': 'Mantenimiento AV',
  
  // Capacitación
  'training_schools': 'Escuela Técnica AV',
  'training_certification': 'Centro de Certificación',
  'training_workshops': 'Workshops y Cursos',
  'training_independent': 'Entrenador Independiente',
  
  // IT / Smart Building
  'it_smart_homes': 'Casas Inteligentes',
  'it_automated_buildings': 'Edificios Automatizados',
  'it_av_over_ip': 'AV sobre IP',
  'it_hybrid': 'Soluciones Híbridas AV-IT',
  
  // Contenidos y Medios
  'media_production': 'Productora Audiovisual',
  'media_streaming': 'Empresa de Streaming',
  'media_recording_studio': 'Estudio de Grabación',
  'media_ar_xr': 'Realidad Aumentada / XR',
  
  // Software
  'software_control': 'Software de Control',
  'software_apps': 'Apps AV',
  'software_monitoring': 'Monitoreo AV Remoto',
  'software_firmware': 'Firmware Personalizado',
  'software_plugins': 'Plugins Audio/Video',
  'software_management': 'Sistemas de Gestión',
  'software_cloud': 'Soluciones AV en la Nube',
  
  // Otros
  'other_cowork': 'Cowork AV',
  'other_marketplace': 'Marketplace AV',
  'other_media': 'Medios Especializados',
  'other_startups': 'Startup AV',
  
  // Fallbacks comunes en inglés que puedan aparecer
  'audio': 'Audio',
  'video': 'Video',
  'lighting': 'Iluminación',
  'production': 'Producción',
  'events': 'Eventos',
  'venue': 'Venue',
  'streaming': 'Streaming',
  'installation': 'Instalación',
  'education': 'Educación',
  'broadcast': 'Broadcast'
};

const ProfileCard = memo(({
  profile,
  variant = 'detailed',
  showActions = true,
  actions = {},
  savedState = false,
  className = '',
  onClick = null,
  followState = null,
  onFollowChange = null,
  enableNavigation = true
}) => {
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detectar el contexto actual
  const currentContext = useMemo(() => {
    if (location.pathname.startsWith('/company')) return 'company';
    if (location.pathname.startsWith('/bronze-member')) return 'bronze-member';
    if (location.pathname.startsWith('/gold-member')) return 'gold-member';
    if (location.pathname.startsWith('/silver-member')) return 'silver-member';
    return 'bronze-member';
  }, [location.pathname]);
  
  // Determinar tipo de perfil
  const profileType = useMemo(() => {
    if (profile.company_name) return 'company';
    if (profile.first_name) return 'technician';
    return 'unknown';
  }, [profile.company_name, profile.first_name]);

  // ✅ Función para traducir servicios
  const translateService = (service) => {
    if (!service) return '';
    
    // Limpiar el servicio de caracteres extraños
    const cleanService = service.toString().trim().toLowerCase();
    
    // Buscar traducción exacta
    if (SERVICE_TRANSLATIONS[cleanService]) {
      return SERVICE_TRANSLATIONS[cleanService];
    }
    
    // Si contiene guión bajo, es probablemente un ID de servicio
    if (cleanService.includes('_')) {
      return SERVICE_TRANSLATIONS[cleanService] || service;
    }
    
    // Para servicios simples, capitalizar primera letra
    return service.charAt(0).toUpperCase() + service.slice(1);
  };

  // Procesar industrias/categorías para empresas
  const processIndustries = (industry, subcategories) => {
    const services = [];
    
    // ✅ SOLO usar subcategories - ignorar industry
    if (subcategories) {
      try {
        let parsedSubcategories;
        
        if (typeof subcategories === 'string') {
          // Manejar formato PostgreSQL {value1,value2}
          if (subcategories.startsWith('{') && subcategories.endsWith('}')) {
            const cleanSub = subcategories.slice(1, -1);
            parsedSubcategories = cleanSub.split(',').map(item => item.trim().replace(/"/g, ''));
          } 
          // Manejar arrays JSON en string
          else if (subcategories.startsWith('[')) {
            parsedSubcategories = JSON.parse(subcategories);
          } 
          // String simple
          else {
            parsedSubcategories = [subcategories];
          }
        } else if (Array.isArray(subcategories)) {
          parsedSubcategories = subcategories;
        } else {
          parsedSubcategories = [subcategories.toString()];
        }
        
        // Traducir y agregar subcategorías
        parsedSubcategories.forEach(service => {
          if (service && service.trim()) {
            services.push(translateService(service.trim()));
          }
        });
        
      } catch (error) {
        console.warn('Error procesando subcategories:', subcategories, error);
        if (subcategories) {
          services.push(translateService(subcategories.toString()));
        }
      }
    }
    
    // Filtrar servicios duplicados y vacíos
    const uniqueServices = [...new Set(services.filter(service => 
      service && 
      service.trim() && 
      service.trim() !== '' && 
      service.trim() !== 'null' && 
      service.trim() !== 'undefined'
    ))];
    
    return uniqueServices.length > 0 ? uniqueServices : ['Empresa AV'];
  };

  // Datos de display optimizados
  const displayData = useMemo(() => {
    switch (profileType) {
      case 'company':
        const services = processIndustries(profile.industry, profile.subcategories);
        const result = {
          name: profile.company_name,
          titles: services, // ✅ Servicios traducidos como títulos
          location: [profile.city, profile.country].filter(Boolean).join(', '),
          imageUrl: profile.logo,
          description: profile.description,
          skills: [], // ✅ No mostrar skills para evitar duplicación
          metadata: {
            size: profile.company_size,
            tier: profile.subscription_tier,
            type: 'empresa',
            hasFranchises: profile.has_franchises,
            category: profile.main_category,
            hasWebsite: profile.slug ? true : false
          },
          contactInfo: {
            website: profile.website,
            internalWebsite: profile.slug ? `/empresa/${profile.slug}` : null
          },
          fallbackIcon: FiBriefcase,
          // ✅ Redirección: sitio web interno > sitio web externo > perfil
          profileUrl: profile.slug 
            ? `/empresa/${profile.slug}` 
            : `/${currentContext}/company/${profile.id}`
        };
        
        // ✅ Debug log para empresas
        console.log('🏢 EMPRESA DISPLAY DATA:', {
          name: result.name,
          slug: profile.slug,
          hasWebsite: result.metadata.hasWebsite,
          profileUrl: result.profileUrl
        });
        
        return result;
      
      case 'technician':
        return {
          name: `${profile.first_name} ${profile.last_name}`,
          titles: Array.isArray(profile.job_title) ? profile.job_title : [profile.job_title || 'Técnico AV'],
          location: [profile.city, profile.country].filter(Boolean).join(', '),
          imageUrl: profile.profile_image_url || (profile.profile_image_path 
            ? `https://izghgwyancrwsuxajfah.supabase.co/storage/v1/object/public/profilepictures/${profile.profile_image_path}`
            : null),
          description: profile.bio,
          skills: profile.skills || [],
          metadata: {
            age: profile.age,
            level: profile.membership_level,
            hasCv: profile.has_cv_enabled,
            type: 'técnico',
            experience: profile.experience_level
          },
          contactInfo: {},
          fallbackIcon: FiUser,
          profileUrl: `/${currentContext}/user/${profile.id}`
        };
      
      default:
        return {
          name: 'Perfil desconocido',
          titles: [],
          location: '',
          imageUrl: null,
          description: '',
          skills: [],
          metadata: {},
          contactInfo: {},
          fallbackIcon: FiUser,
          profileUrl: '#'
        };
    }
  }, [profile, profileType, currentContext]);

  // Iniciales para avatar
  const initials = useMemo(() => {
    return displayData.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [displayData.name]);

  // Estilos por variante
  const variantStyles = useMemo(() => {
    const base = "bg-dark-800 rounded-xl border border-dark-700 hover:border-primary-500 transition-all duration-300 shadow-lg hover:shadow-xl relative group";
    
    switch (variant) {
      case 'compact':
        return `${base} p-4`;
      case 'grid':
        return `${base} p-5 h-full`;
      case 'detailed':
      default:
        return `${base} p-5`;
    }
  }, [variant]);

  // ✅ Manejar click en la card con redirección inteligente
  const handleCardClick = (e) => {
    console.log('🔥 CLICK EN CARD:', {
      profileType,
      slug: profile.slug,
      hasSlug: !!profile.slug,
      target: e.target.tagName,
      closest: e.target.closest('button')
    });
    
    // Prevenir si se hace click en botones
    if (e.target.closest('button') || e.target.closest('a')) {
      console.log('🚫 Click en botón/enlace, ignorando');
      return;
    }
    
    // Si hay onClick personalizado, usarlo
    if (onClick) {
      console.log('🎯 Ejecutando onClick personalizado');
      onClick(profile);
      return;
    }
    
    // ✅ PRIORIDAD: Para empresas con sitio web, ir directo a la web
    if (profileType === 'company' && profile.slug) {
      const websiteUrl = `/empresa/${profile.slug}`;
      console.log('🌐 NAVEGANDO A SITIO WEB:', websiteUrl);
      window.location.href = websiteUrl;
      return;
    }
    
    // ✅ FALLBACK: Si no tiene web, usar onViewDetails si existe
    if (profileType === 'company' && actions.onViewDetails) {
      console.log('📋 Abriendo modal de detalles');
      actions.onViewDetails(profile);
      return;
    }
    
    // ✅ ÚLTIMO RECURSO: Navegación normal
    if (enableNavigation && displayData.profileUrl !== '#') {
      console.log('🚀 Navegando a perfil normal:', displayData.profileUrl);
      navigate(displayData.profileUrl);
    } else {
      console.log('❌ No hay navegación configurada');
    }
  };

  // Truncar texto
  const truncateText = useMemo(() => {
    return (text, variant) => {
      if (!text) return '';
      const limits = { compact: 80, detailed: 120, grid: 100 };
      const limit = limits[variant] || limits.detailed;
      return text.length > limit ? `${text.slice(0, limit)}...` : text;
    };
  }, []);

  // ✅ Renderizar avatar/logo SIN INDICADOR DE HOVER
  const renderAvatar = () => {
    const sizeClasses = {
      compact: 'w-12 h-12',
      detailed: 'w-14 h-14',
      grid: 'w-16 h-16'
    };
    
    const size = sizeClasses[variant] || sizeClasses.detailed;
    const FallbackIcon = displayData.fallbackIcon;

    return (
      <div className={`${size} rounded-full bg-dark-700 flex items-center justify-center overflow-hidden flex-shrink-0 relative`}>
        {displayData.imageUrl ? (
          <img
            src={displayData.imageUrl}
            alt={displayData.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full ${displayData.imageUrl ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-primary-900 to-primary-700`}>
          {profileType === 'company' ? (
            <FallbackIcon className="text-white" size={variant === 'compact' ? 16 : 20} />
          ) : (
            <span className={`font-bold text-white ${variant === 'compact' ? 'text-sm' : 'text-lg'}`}>
              {initials}
            </span>
          )}
        </div>
      </div>
    );
  };

  // ✅ Renderizar títulos/servicios (MEJORADO)
  const renderTitles = () => {
    if (!displayData.titles.length) return null;
    
    const maxTitles = variant === 'compact' ? 2 : 3; // Mostrar más títulos
    const visibleTitles = displayData.titles.slice(0, maxTitles);
    const remainingCount = displayData.titles.length - maxTitles;

    return (
      <div className="text-primary-400 text-sm space-y-1">
        {visibleTitles.map((title, idx) => (
          <div key={idx} className="flex items-center">
            <span className="mr-2 text-primary-500 text-xs">•</span>
            <span className="truncate text-xs">{title}</span>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="text-gray-400 text-xs ml-4">
            +{remainingCount} más
          </div>
        )}
      </div>
    );
  };

  // ✅ Renderizar metadata (SIN BADGE BRONCE por defecto)
  const renderMetadata = () => {
    const { age, level, hasCv, size, tier, hasFranchises, category, type, experience, hasWebsite } = displayData.metadata;
    
    return (
      <div className="flex items-center gap-2 flex-wrap mt-1">
        {/* Para técnicos */}
        {age && (
          <span className="text-gray-400 text-xs">
            {age} años
          </span>
        )}
        {experience && (
          <span className="text-blue-400 text-xs capitalize">
            {experience}
          </span>
        )}
        
        {/* Para empresas */}
        {size && (
          <span className="text-gray-400 text-xs capitalize flex items-center">
            <FiUsers size={10} className="mr-1" />
            {size === 'micro' ? 'Micro (1-10)' :
             size === 'small' ? 'Pequeña (11-50)' :
             size === 'medium' ? 'Mediana (51-250)' :
             size === 'large' ? 'Grande (250+)' :
             'Tamaño no especificado'}
          </span>
        )}
        {hasFranchises && (
          <span className="text-green-400 text-xs flex items-center">
            <FiTrendingUp size={10} className="mr-1" />
            Franquicias
          </span>
        )}
        {hasWebsite && (
          <span className="text-primary-400 text-xs flex items-center bg-primary-900/30 px-2 py-1 rounded-full border border-primary-600/30">
            <FiGlobe size={10} className="mr-1" />
            Sitio web
          </span>
        )}
        
        {hasCv && (
          <span className="text-green-400 text-xs flex items-center">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
            CV disponible
          </span>
        )}
      </div>
    );
  };

  // Renderizar info de contacto (solo para empresas en variant detailed)
  const renderContactInfo = () => {
    if (profileType !== 'company' || variant === 'compact') return null;
    
    const { website, internalWebsite } = displayData.contactInfo;
    
    // Solo mostrar website si no hay website interno configurado
    if (!website || internalWebsite) return null;

    return (
      <div className="flex items-center gap-2 mt-2">
        <a
          href={website.startsWith('http') ? website : `https://${website}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-primary-400 transition-colors flex items-center text-xs"
        >
          <FiGlobe size={12} className="mr-1" />
          <span className="truncate">Sitio web externo</span>
        </a>
      </div>
    );
  };

  // ✅ NO renderizar skills para empresas (evitar duplicación)
  const renderSkills = () => {
    // Solo mostrar skills para técnicos, no para empresas
    if (profileType === 'company' || !displayData.skills.length) return null;
    
    const maxSkills = variant === 'compact' ? 2 : 3;
    const visibleSkills = displayData.skills.slice(0, maxSkills);
    const remainingCount = displayData.skills.length - maxSkills;

    return (
      <div className="flex flex-wrap gap-1.5 mt-3">
        {visibleSkills.map((skill, index) => (
          <span
            key={index}
            className="px-2.5 py-1 rounded-full text-xs font-medium bg-dark-700 text-gray-300 border border-dark-600"
          >
            {skill}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-dark-700 text-gray-400 border border-dark-600">
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  // Renderizar acciones - SIN ESTRELLA
  const renderActions = () => {
    if (!showActions) return null;

    return (
      <div className="flex items-center gap-2 mt-4">
        {actions.onContact && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              actions.onContact(profile);
            }}
            className="flex-1 py-2.5 px-4 bg-primary-600 hover:bg-primary-500 text-white rounded-lg flex items-center justify-center text-sm font-medium transition-colors"
          >
            <FiMessageSquare className="mr-2" size={16} />
            Contactar
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className={`${variantStyles} cursor-pointer ${className}`} // ✅ Forzar cursor pointer
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
    >
      <div className="h-full flex flex-col">
        {/* Header con avatar y info básica */}
        <div className="flex items-start mb-3">
          {renderAvatar()}
          
          <div className="flex-1 min-w-0 ml-4">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0 pr-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-bold text-lg text-white leading-tight truncate group-hover:text-primary-400 transition-colors">
                    {displayData.name}
                  </h3>
                  <FlagBadge country={profile.country} size="sm" className="flex-shrink-0" />
                </div>
                
                {/* ✅ Ubicación MÁS RELEVANTE - movida arriba */}
                {displayData.location && (
                  <p className="text-gray-300 text-sm flex items-center mt-1 mb-2">
                    <FiMapPin size={12} className="mr-1 flex-shrink-0" />
                    <span className="truncate font-medium">{displayData.location}</span>
                  </p>
                )}
                
                {renderMetadata()}
              </div>
              
              {/* FollowButton */}
              {actions.onFollow !== false && (
                <div className="flex-shrink-0">
                  <FollowButton
                    profileId={profile.id}
                    profileType={profileType}
                    variant="icon"
                    initialFollowState={followState}
                    onFollowChange={onFollowChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
            
            {/* Títulos/servicios */}
            {renderTitles()}
            
            {/* Info de contacto para empresas */}
            {renderContactInfo()}
          </div>
        </div>

        {/* Descripción */}
        {displayData.description && variant !== 'compact' && (
          <p className="text-gray-300 text-sm mb-3 line-clamp-2">
            {truncateText(displayData.description, variant)}
          </p>
        )}

        {/* Skills y acciones en la parte inferior */}
        <div className="mt-auto">
          {renderSkills()}
          {renderActions()}
        </div>
      </div>
    </div>
  );
});

ProfileCard.displayName = 'ProfileCard';

export default ProfileCard;